import { View } from 'react-native';
import { useStyles } from 'react-native-unistyles';

import { openLinkInBrowser } from '@/core';
import { Text } from '@/ui';

import {
  AUTONOMOUS_DRIVING_PARAGRAPHS,
  CAR_CONDITIONS_RIGHTS_PARAGRAPHS,
  DEFINITIONS_PARAGRAPH,
  GENERIC_TOC_ITEMS,
  RENTAL_TOC_ITEMS,
  SUBSCRIPTION_SERVICES_PARAGRAPHS,
} from './copy';

export type { RentalTocItem } from './copy';

export function RentalTermsSummary() {
  const { theme } = useStyles();

  return (
    <View style={{ gap: 16 }}>
      <Text type="title1">Terms And Conditions</Text>

      <View style={{ gap: 24 }}>
        {GENERIC_TOC_ITEMS.map((item, index) => {
          const segments = typeof item === 'string' ? [item] : item;

          return (
            <View key={`toc-${index}`}>
              <Text
                type="bodyRegular"
                style={{ color: theme.colors.text.secondary }}
              >
                {`${index + 1}. `}
                {segments.map((segment, segmentIndex) =>
                  typeof segment === 'string' ? (
                    segment
                  ) : (
                    <Text
                      key={`toc-${index}-link-${segmentIndex}`}
                      type="bodyRegular"
                      style={{
                        color: theme.colors.text.secondary,
                        textDecorationLine: 'underline',
                      }}
                      onPress={() => openLinkInBrowser(segment.url)}
                    >
                      {segment.text}
                    </Text>
                  )
                )}
              </Text>
            </View>
          );
        })}
      </View>
    </View>
  );
}

export function RentalTermsDetails() {
  const { theme } = useStyles();

  return (
    <View style={{ gap: 32 }}>
      <View style={{ gap: 16 }}>
        <Text type="title1">Rental Agreement Terms and Conditions</Text>

        <Text type="bodyRegular" style={{ color: theme.colors.text.secondary }}>
          {DEFINITIONS_PARAGRAPH}
        </Text>

        <View style={{ gap: 24 }}>
          {RENTAL_TOC_ITEMS.map((item, index) => (
            <View key={`rental-toc-${index}`} style={{ gap: 12 }}>
              <Text
                type="bodyRegular"
                style={{ color: theme.colors.text.secondary }}
              >
                {`${index + 1}. ${item.text}`}
              </Text>

              {item.bullets?.map((bullet, bulletIndex) => (
                <View
                  key={`rental-toc-${index}-bullet-${bulletIndex}`}
                  style={{
                    flexDirection: 'row',
                    marginLeft: 16,
                  }}
                >
                  <Text
                    type="bodyRegular"
                    style={{
                      marginRight: 8,
                      color: theme.colors.text.secondary,
                    }}
                  >
                    {'\u2022'}
                  </Text>

                  <Text
                    type="bodyRegular"
                    style={{
                      flex: 1,
                      color: theme.colors.text.secondary,
                    }}
                  >
                    {bullet}
                  </Text>
                </View>
              ))}
            </View>
          ))}
        </View>
      </View>

      <View style={{ gap: 16 }}>
        <Text type="title1">
          Autonomous and Semi-Autonomous Driving Disclaimer
        </Text>

        <View style={{ gap: 16 }}>
          {AUTONOMOUS_DRIVING_PARAGRAPHS.map((paragraph, index) => (
            <Text
              key={`autonomous-${index}`}
              type="bodyRegular"
              style={{ color: theme.colors.text.secondary }}
            >
              {paragraph}
            </Text>
          ))}
        </View>
      </View>

      <View style={{ gap: 16 }}>
        <Text type="title1">Subscription Services</Text>

        <View style={{ gap: 16 }}>
          {SUBSCRIPTION_SERVICES_PARAGRAPHS.map((paragraph, index) => (
            <Text
              key={`subscription-${index}`}
              type="bodyRegular"
              style={{ color: theme.colors.text.secondary }}
            >
              {paragraph}
            </Text>
          ))}
        </View>
      </View>

      <View style={{ gap: 16 }}>
        <Text type="title1">Car Conditions - Your Rights Before Pickup</Text>

        <View style={{ gap: 16 }}>
          {CAR_CONDITIONS_RIGHTS_PARAGRAPHS.map((paragraph, index) => (
            <Text
              key={`car-conditions-${index}`}
              type="bodyRegular"
              style={{ color: theme.colors.text.secondary }}
            >
              {paragraph}
            </Text>
          ))}
        </View>
      </View>
    </View>
  );
}

// Full generic rental-terms document: summary block followed by the full
// legal body. Use this on standalone pages like /rental-terms. For
// reservation pages that need to interleave reservation-specific cards
// between the summary and the body, render `<RentalTermsSummary />` and
// `<RentalTermsDetails />` separately.
export function RentalTerms() {
  return (
    <View style={{ gap: 32 }}>
      <RentalTermsSummary />
      <RentalTermsDetails />
    </View>
  );
}
